import { useEffect, useMemo, useRef, useState } from 'react'

import { Heading, Text, theme } from '@hhgtech/hhg-components'
import { Chart as ChartJS } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { gsap } from 'gsap'
import throttle from 'lodash/throttle'
import { useRouter } from 'next/router'
import { Chart } from 'react-chartjs-2'
import { useIntl } from 'react-intl'

import { assetImagePath } from 'interfaces/constants'

import {
  StyledContainer,
  StyledDataContainer,
  StyledMapWrapper,
  StyledPieChart,
  StyledWeightCharts,
  StyledLabelInfoContainer,
  StyledLegendBox,
  StyledLegendContainer,
  StyledLegendDesktopContainer,
  StyledHeadingContainer,
  StyledPreviousMap,
  StyledNextMap,
  StyledDotsContainer,
  StyledDot,
  StyledPaging,
} from './index.styled'

import 'chart.js/auto'
ChartJS.register(ChartDataLabels)

type ObeseDataProps = {
  country: string
  locale: string
  bannerImgDesktop: string
  bannerImgMobile: string
  datasets: {
    obeses?: {
      label: string
      value: number
    }[]
    weight?: {
      normalWeight?: number
      borderline?: number
    }
  }
}

type Props = {
  isMobile: boolean
  topbarHeight?: number
}

const BlockingAnimationTime = 1000

const ObesityPercentage = ({ isMobile, topbarHeight = 64 }: Props) => {
  const { formatMessage: f } = useIntl()
  const { locale = 'en-PH' } = useRouter()
  const [hasScrollToView, setHasScrollToView] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const [bannerHeight, setBannerHeight] = useState(isMobile ? 603 : 831)

  const [contentPadding, setContentPadding] = useState(1051)

  const enableSlide = useRef(true)

  const enableWheel = useRef(true)

  const tl = useRef({})
  const indx = useRef({
    index: 0,
  })
  const el = useRef<HTMLDivElement>(null)
  const q = gsap.utils.selector(el)

  const swipeBegin = useRef({
    x: -1,
    y: -1,
  })

  const obesityData: ObeseDataProps[] = useMemo(
    () => [
      {
        country: 'Thailand',
        locale: 'th-TH',
        bannerImgDesktop: `${assetImagePath}/obesity-thailand-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-thailand-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 19.76,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 9.34,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 1.2,
            },
          ],
        },
      },
      {
        country: 'Vietnam',
        locale: 'vi-VN',
        bannerImgDesktop: `${assetImagePath}/obesity-vietnam-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-vietnam-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 14.04,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 3.16,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 1.38,
            },
          ],
          weight: {
            normalWeight: 17.83,
            borderline: 43.74,
          },
        },
      },
      {
        country: 'Taiwan',
        locale: 'zh-TW',
        bannerImgDesktop: `${assetImagePath}/obesity-taiwan-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-taiwan-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 20.98,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 6.63,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 0.88,
            },
          ],
          weight: {
            normalWeight: 19.23,
            borderline: 42.64,
          },
        },
      },
      {
        country: 'Philippines',
        locale: 'en-PH',
        bannerImgDesktop: `${assetImagePath}/obesity-philippines-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-philippines-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 21.57,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 9.64,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 5.33,
            },
          ],
          weight: {
            normalWeight: 13.71,
          },
        },
      },
      {
        country: 'Malaysia',
        locale: 'ms-MY',
        bannerImgDesktop: `${assetImagePath}/obesity-malaysia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-malaysia-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 23.43,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 13.47,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 2.01,
            },
          ],
        },
      },
      {
        country: 'Indonesia',
        locale: 'id-ID',
        bannerImgDesktop: `${assetImagePath}/obesity-indonesia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-indonesia-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 16.05,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 5.94,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 1.08,
            },
          ],
        },
      },
      {
        country: 'India',
        locale: 'hi-IN',
        bannerImgDesktop: `${assetImagePath}/obesity-india-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-india-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 27.19,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 10.82,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 5.35,
            },
          ],
        },
      },
      {
        country: 'Cambodia',
        locale: 'km-KH',
        bannerImgDesktop: `${assetImagePath}/obesity-cambodia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/obesity-cambodia-mobile.png?v=1`,
        datasets: {
          obeses: [
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 1,
                },
              ),
              value: 17.62,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 2,
                },
              ),
              value: 4.45,
            },
            {
              label: f(
                {
                  id: 'superContent.obesity.obeseName',
                  defaultMessage: 'Obese {number}',
                },
                {
                  number: 3,
                },
              ),
              value: 1.37,
            },
          ],
          weight: {
            normalWeight: 17.79,
            borderline: 43.603,
          },
        },
      },
    ],
    [],
  )

  const data = useMemo(
    () => [
      ...obesityData.filter((el) => el.locale === locale),
      ...obesityData.filter((el) => el.locale !== locale),
    ],
    [obesityData],
  )

  const slideDown = () => {
    if (enableSlide) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .to(q('.animation-image-post'), {
          opacity: 0,
          duration: 1,
        })
        .to(
          q('.animation-image-main'),
          {
            opacity: 1,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: -50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 0.01 },
            duration: 1,
          },
          0,
        )
    }
  }

  const slideUp = () => {
    if (enableSlide) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)

      tl.current = gsap
        .timeline()
        .to(q('.animation-image-main'), {
          opacity: 1,
          duration: 1,
        })
        .to(
          '.animation-image-after',
          {
            opacity: 0,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: -50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 0.01 },
            duration: 1,
          },
          0,
        )
    }
  }
  useEffect(() => {
    if (indx.current.index <= currentIndex) {
      slideDown()
    } else {
      slideUp()
    }
    indx.current.index = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.innerHeight > 0) {
      const bannerHeight = isMobile
        ? window?.innerHeight
        : window?.innerHeight - topbarHeight
      const bannerWidth = (bannerHeight * 1440) / 955
      setBannerHeight(bannerHeight)
      setContentPadding((window.innerWidth - bannerWidth) / 2)
    }
  }, [])

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!hasScrollToView && typeof window !== 'undefined') {
        const winScroll =
          document.body.scrollTop || document.documentElement.scrollTop

        if (
          winScroll - (el?.current?.offsetTop || 0) + window.innerHeight - 100 >
          0
        ) {
          setHasScrollToView(true)
          window.removeEventListener('scroll', handleScroll)
        }
      }
    }, 200)
    if (
      (el?.current?.offsetTop || 0) <
      (typeof window !== 'undefined' ? window.innerHeight : 1000)
    ) {
      setHasScrollToView(true)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (hasScrollToView) {
      tl.current = gsap
        .timeline()
        .fromTo(
          q('.heading'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
          },
        )
        .fromTo(
          q('.description'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
          },
        )
        .from(
          q('.legend'),
          {
            x: 50,
            duration: 0.8,
            opacity: 0,
          },
          0.5,
        )
    }
  }, [hasScrollToView])

  const obeseConfigs = [
    {
      index: 1,
      range: '30 - 34.9',
    },
    {
      index: 2,
      range: '35.0 - 39.9',
    },
    {
      index: 3,
      range: '>= 40',
    },
  ]

  const currentCountry = data?.[currentIndex]

  const handleWheel = (type: 'next' | 'previous') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      if (type === 'next') {
        typeof window !== 'undefined' &&
          currentIndex < data?.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) =>
          v > data?.length - 2 ? data?.length - 1 : v + 1,
        )
      } else {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      }
    }
  }

  const doughnutChartOption = {
    cutout: '80%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },

      datalabels: {
        display: false,
      },
    },
    animation: {
      duration: BlockingAnimationTime,
      easing: 'easeInQuint',
    },
  }

  const barChartOption = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      xAxis: {
        display: false,
        suggestedMax:
          (currentCountry?.datasets?.weight?.normalWeight || 0) >
          (currentCountry?.datasets?.weight?.borderline || 0)
            ? currentCountry?.datasets?.weight?.normalWeight
            : currentCountry?.datasets?.weight?.borderline,
      },
      yAxis: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        anchor: 'end',
        offset: 8,
        align: 'end',
        color: (ctx: any) => {
          return (ctx?.dataset?.backgroundColor as string) || 'blue'
        },
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value: any) => {
          return value ? `${value}%` : ''
        },
      },
    },
    layout: {
      height: 24,
      padding: {
        right: 42,
      },
    },
    animation: {
      duration: BlockingAnimationTime,
      easing: 'easeInQuint',
    },
  }

  const handleChangeCountry = (newIndex: number) => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      typeof window !== 'undefined' &&
        window.scrollTo({
          top: isMobile
            ? el?.current?.offsetTop
            : (el?.current?.offsetTop || 64) - 64,
          behavior: 'smooth',
        })

      setCurrentIndex(newIndex)
    }
  }

  const handleSlide = (type: 'left' | 'right') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      if (type === 'left') {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      } else {
        typeof window !== 'undefined' &&
          currentIndex < data?.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })
        setCurrentIndex((v) =>
          v > data?.length - 2 ? data?.length - 1 : v + 1,
        )
      }

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)
    }
  }

  // const handleChangeCountry = (newIndex: number) => {
  //   if (enableWheel.current && enableSlide.current) {
  //     enableWheel.current = false

  //     setTimeout(() => {
  //       enableWheel.current = true
  //     }, BlockingAnimationTime)

  //     typeof window !== 'undefined' &&
  //       window.scrollTo({
  //         top: isMobile
  //           ? el?.current?.offsetTop
  //           : (el?.current?.offsetTop || 64) - 64,
  //         behavior: 'smooth',
  //       })

  //     setCurrentIndex(newIndex)
  //   }
  // }

  return (
    <StyledContainer
      data-banner-height={bannerHeight}
      ref={el}
      onTouchStart={(e) => {
        swipeBegin.current.x = e.changedTouches[0].clientX
        swipeBegin.current.y = e.changedTouches[0].clientY
      }}
      onTouchEnd={(e) => {
        if (
          swipeBegin.current.x > 0 &&
          Math.abs(e.changedTouches[0].clientX - swipeBegin.current.x) >
            Math.abs(e.changedTouches[0].clientY - swipeBegin.current.y)
        ) {
          handleSlide(
            e.changedTouches[0].clientX - swipeBegin.current.x > 0
              ? 'left'
              : 'right',
          )
        }
        swipeBegin.current.x = -1
        swipeBegin.current.y = -1
      }}
    >
      {isMobile ? (
        <>
          <Heading
            className="heading"
            tag="h2"
            color={theme.colors.primaryBase}
          >
            {f({
              id: 'superContent.obesity.title',
              defaultMessage:
                'BMI Calculator Results: Countries and their Obesity Percentages',
            })}
          </Heading>
          <Text
            size="p4"
            className="description"
            color={theme.colors.gray500}
            style={{ marginTop: 12 }}
          >
            {f({
              id: 'superContent.obesity.description',
              defaultMessage:
                'Based on interactions with Hello Health’s BMI calculator from 30 January to 29 July 2021',
            })}
          </Text>
          <StyledMapWrapper>
            {data?.map((data, index) => (
              <img
                loading="lazy"
                key={index}
                className={`animation animation-image${
                  index < currentIndex ? ' animation-image-post' : ''
                }${currentIndex === index ? ' animation-image-main' : ''}${
                  currentIndex < index ? ' animation-image-after' : ''
                }`}
                src={isMobile ? data?.bannerImgMobile : data?.bannerImgDesktop}
                alt={f({
                  id: 'superContent.obesity.title',
                  defaultMessage:
                    'BMI Calculator Results: Countries and their Obesity Percentages',
                })}
              />
            ))}
            <StyledDataContainer data-content-padding={contentPadding}>
              <div className="country-title">{currentCountry?.country}</div>
              {currentCountry?.datasets?.obeses &&
              currentCountry?.datasets?.obeses?.length > 0 ? (
                <div className="charts-wrapper">
                  {currentCountry?.datasets?.obeses.map((obese, index) => (
                    <StyledPieChart key={index}>
                      <div className="chart-container">
                        <Chart
                          type="doughnut"
                          data={{
                            labels: ['active', 'inactive'],
                            datasets: [
                              {
                                label: obese?.label,
                                data: [obese?.value, 100 - obese?.value],
                                backgroundColor: ['#2D87F3', '#E4E8EC'],
                                borderWidth: 0,
                                hoverOffset: 4,
                                borderRadius: 6,
                              },
                            ],
                          }}
                          options={doughnutChartOption as any}
                        />
                        <div className="data-label">
                          <div className="value-number">{obese?.value}</div>
                          {'%'}
                        </div>
                      </div>
                      <div className="chart-name">{obese?.label}</div>
                    </StyledPieChart>
                  ))}
                </div>
              ) : null}
              {currentCountry?.datasets?.weight?.borderline ||
              currentCountry?.datasets?.weight?.normalWeight ? (
                <StyledWeightCharts>
                  {currentCountry?.datasets?.weight?.normalWeight ? (
                    <div className="bar-container">
                      <img
                        alt=""
                        loading="lazy"
                        className="bar-label"
                        src={`${assetImagePath}/weight.svg`}
                      />
                      <Chart
                        type="bar"
                        data={{
                          labels: ['weight'],
                          datasets: [
                            {
                              data: [
                                currentCountry?.datasets?.weight?.normalWeight,
                              ],
                              backgroundColor: '#FEA21E',
                              barThickness: 12,
                              borderRadius: Number.MAX_VALUE,
                              borderSkipped: false,
                            },
                          ],
                        }}
                        options={barChartOption as any}
                      />
                    </div>
                  ) : null}
                  {currentCountry?.datasets?.weight?.borderline ? (
                    <div className="bar-container">
                      <img
                        alt=""
                        loading="lazy"
                        className="bar-label"
                        src={`${assetImagePath}/bmi-human.svg`}
                      />
                      <Chart
                        type="bar"
                        data={{
                          labels: ['weight'],
                          datasets: [
                            {
                              data: [
                                currentCountry?.datasets?.weight?.borderline,
                              ],
                              backgroundColor: '#2D87F3',
                              barThickness: 12,
                              borderRadius: Number.MAX_VALUE,
                              borderSkipped: false,
                            },
                          ],
                        }}
                        options={barChartOption as any}
                      />
                    </div>
                  ) : null}
                </StyledWeightCharts>
              ) : null}
            </StyledDataContainer>

            <StyledLabelInfoContainer>
              {obeseConfigs.map((obese, i) => (
                <div className="info" key={i}>
                  <b>
                    {f(
                      {
                        id: 'superContent.obesity.obeseName',
                        defaultMessage: 'Obese {number}',
                      },
                      {
                        number: obese?.index,
                      },
                    )}
                    {':'}
                  </b>{' '}
                  {f(
                    {
                      id: 'superContent.obesity.obeseRange',
                      defaultMessage: 'BMI of {range}',
                    },
                    {
                      range: obese?.range,
                    },
                  )}
                </div>
              ))}
            </StyledLabelInfoContainer>
          </StyledMapWrapper>
          {currentCountry?.datasets?.weight?.borderline ||
          currentCountry?.datasets?.weight?.normalWeight ? (
            <StyledLegendContainer>
              <div className="legend">
                <StyledLegendBox style={{ backgroundColor: '#FEA21E' }} />
                <div className="legend-label">
                  <b>
                    {f({
                      id: 'superContent.obesity.legends.normalWeight',
                      defaultMessage: 'Normal weight BMI of 18.5 - 24.9',
                    })}
                  </b>
                </div>
              </div>
              <div className="legend">
                <StyledLegendBox style={{ backgroundColor: '#2D87F3' }} />
                <div className="legend-label">
                  <b>
                    {f({
                      id: 'superContent.obesity.legends.borderline',
                      defaultMessage:
                        'Borderline overweight BMI of 25.0 - <30.0',
                    })}
                  </b>
                </div>
              </div>
            </StyledLegendContainer>
          ) : null}
          <StyledDotsContainer>
            {data?.map((e, index) => (
              <StyledDot
                key={`${e?.country}-${index}`}
                data-active={index === currentIndex}
                onClick={() => handleChangeCountry(index)}
              ></StyledDot>
            ))}
          </StyledDotsContainer>
        </>
      ) : (
        <>
          {data?.map((data, index) => (
            <img
              key={index}
              loading="lazy"
              className={`animation animation-image${
                index < currentIndex ? ' animation-image-post' : ''
              }${currentIndex === index ? ' animation-image-main' : ''}${
                currentIndex < index ? ' animation-image-after' : ''
              }`}
              src={isMobile ? data?.bannerImgMobile : data?.bannerImgDesktop}
              alt={f({
                id: 'superContent.obesity.title',
                defaultMessage:
                  'BMI Calculator Results: Countries and their Obesity Percentages',
              })}
            />
          ))}
          <StyledHeadingContainer data-content-padding={contentPadding}>
            <Heading
              className="heading"
              tag="h2"
              color={theme.colors.primaryBase}
            >
              {f({
                id: 'superContent.obesity.title',
                defaultMessage:
                  'BMI Calculator Results: Countries and their Obesity Percentages',
              })}
            </Heading>
            <Text
              size="p4"
              className="description"
              color={theme.colors.gray500}
            >
              {f({
                id: 'superContent.obesity.description',
                defaultMessage:
                  'Based on interactions with Hello Health’s BMI calculator from 30 January to 29 July 2021',
              })}
            </Text>
          </StyledHeadingContainer>
          <StyledDataContainer data-content-padding={contentPadding}>
            <div className="country-title">{currentCountry?.country}</div>
            {currentCountry?.datasets?.obeses &&
            currentCountry?.datasets?.obeses?.length > 0 ? (
              <div className="charts-wrapper">
                {currentCountry?.datasets?.obeses.map((obese, index) => (
                  <StyledPieChart key={index}>
                    <div className="chart-container">
                      <Chart
                        type="doughnut"
                        data={{
                          labels: ['active', 'inactive'],
                          datasets: [
                            {
                              label: obese?.label,
                              data: [obese?.value, 100 - obese?.value],
                              backgroundColor: ['#2D87F3', '#E4E8EC'],
                              borderWidth: 0,
                              hoverOffset: 4,
                              borderRadius: 6,
                            },
                          ],
                        }}
                        options={doughnutChartOption as any}
                      />
                      <div className="data-label">
                        <div className="value-number">{obese?.value}</div>
                        {'%'}
                      </div>
                    </div>
                    <div className="chart-name">{obese?.label}</div>
                  </StyledPieChart>
                ))}
              </div>
            ) : null}
            {currentCountry?.datasets?.weight?.borderline ||
            currentCountry?.datasets?.weight?.normalWeight ? (
              <StyledWeightCharts>
                {currentCountry?.datasets?.weight?.normalWeight ? (
                  <div className="bar-container">
                    <img
                      alt=""
                      loading="lazy"
                      className="bar-label"
                      src={`${assetImagePath}/weight.svg`}
                    />
                    <Chart
                      type="bar"
                      data={{
                        labels: ['weight'],
                        datasets: [
                          {
                            data: [
                              currentCountry?.datasets?.weight?.normalWeight,
                            ],
                            backgroundColor: '#FEA21E',
                            barThickness: 24,
                            borderRadius: 6,
                            borderSkipped: false,
                          },
                        ],
                      }}
                      options={barChartOption as any}
                    />
                  </div>
                ) : null}
                {currentCountry?.datasets?.weight?.borderline ? (
                  <div className="bar-container">
                    <img
                      alt=""
                      loading="lazy"
                      className="bar-label"
                      src={`${assetImagePath}/bmi-human.svg`}
                    />
                    <Chart
                      type="bar"
                      data={{
                        labels: ['weight'],
                        datasets: [
                          {
                            data: [
                              currentCountry?.datasets?.weight?.borderline,
                            ],
                            backgroundColor: '#2D87F3',
                            barThickness: 24,
                            borderRadius: 6,
                            borderSkipped: false,
                          },
                        ],
                      }}
                      options={barChartOption as any}
                    />
                  </div>
                ) : null}
              </StyledWeightCharts>
            ) : null}
          </StyledDataContainer>
          <StyledLegendDesktopContainer data-content-padding={contentPadding}>
            <StyledLabelInfoContainer>
              {obeseConfigs.map((obese, i) => (
                <div className="info" key={i}>
                  <b>
                    {f(
                      {
                        id: 'superContent.obesity.obeseName',
                        defaultMessage: 'Obese {number}',
                      },
                      {
                        number: obese?.index,
                      },
                    )}
                    {':'}
                  </b>{' '}
                  {f(
                    {
                      id: 'superContent.obesity.obeseRange',
                      defaultMessage: 'BMI of {range}',
                    },
                    {
                      range: obese?.range,
                    },
                  )}
                </div>
              ))}
            </StyledLabelInfoContainer>
            <StyledLegendContainer>
              {currentCountry?.datasets?.weight?.borderline ||
              currentCountry?.datasets?.weight?.normalWeight ? (
                <>
                  <div className="legend">
                    <StyledLegendBox style={{ backgroundColor: '#FEA21E' }} />
                    <div className="legend-label">
                      <b>
                        {f({
                          id: 'superContent.obesity.legends.normalWeight',
                          defaultMessage: 'Normal weight BMI of 18.5 - 24.9',
                        })}
                      </b>
                    </div>
                  </div>
                  <div className="legend">
                    <StyledLegendBox style={{ backgroundColor: '#2D87F3' }} />
                    <div className="legend-label">
                      <b>
                        {f({
                          id: 'superContent.obesity.legends.borderline',
                          defaultMessage:
                            'Borderline overweight BMI of 25.0 - <30.0',
                        })}
                      </b>
                    </div>
                  </div>
                </>
              ) : null}
            </StyledLegendContainer>
          </StyledLegendDesktopContainer>
          {currentIndex !== 0 ? (
            <StyledPreviousMap>
              <img
                loading="lazy"
                src={`${assetImagePath}/chevronLeftCircle.svg`}
                alt="previous"
                onClick={() => handleWheel('previous')}
              />
            </StyledPreviousMap>
          ) : null}
          {currentIndex < data?.length - 1 ? (
            <StyledNextMap>
              <img
                loading="lazy"
                src={`${assetImagePath}/chevronRightCircle.svg`}
                alt="next"
                onClick={() => handleWheel('next')}
              />
            </StyledNextMap>
          ) : null}
          <StyledPaging data-content-padding={contentPadding}>
            <b>{currentIndex + 1}</b>/{data.length}
          </StyledPaging>
        </>
      )}
    </StyledContainer>
  )
}

export { ObesityPercentage }
